import type { Feature, Polygon, Position } from "geojson";
import { polygon } from "@turf/helpers";
import { polygonContains, polygonOverlaps } from "../geometry/operations/validate";
import type { AnyPolygonFeature, GeometryResult } from "../geometry/types";
import type { Zone } from "./Zone";
import type { ZoneCollection } from "./ZoneCollection";

/**
 * Validates that a zone is fully inside the boundary.
 * Accepts a raw Feature so callers don't need to wrap the boundary in a Zone.
 * Supports MultiPolygon boundaries — zone must be inside at least one polygon part.
 */
export function validateZonePlacement(zone: Zone, boundary: AnyPolygonFeature): GeometryResult<void> {
	if (!polygonContains(boundary, zone.geometry)) {
		return { ok: false, error: "Zone must be inside boundary" };
	}
	return { ok: true, data: undefined };
}

/**
 * Validates that a hole ring is fully inside the target polygon's outer ring
 * and does not intersect existing holes.
 */
export function validateHolePlacement(hole: Position[], zone: Zone, polyIndex: number): GeometryResult<void> {
	const outerRing = zone.getOuterRing(polyIndex);
	const outerOnly = polygon([outerRing]);
	const holePoly = polygon([hole]);

	// Hole must be inside outer ring
	if (!polygonContains(outerOnly, holePoly)) {
		return { ok: false, error: "Hole must be inside zone outer ring" };
	}

	// Hole must not overlap existing holes
	const existingHoles = zone.getHoles(polyIndex);
	for (const existingHole of existingHoles) {
		const existingPoly = polygon([existingHole]);
		const { overlaps } = polygonOverlaps(holePoly, existingPoly);
		if (overlaps) {
			return { ok: false, error: "Hole intersects existing hole" };
		}
	}

	return { ok: true, data: undefined };
}

/**
 * Auto-classify a new polygon as either a hole in an existing zone
 * or a new outer boundary.
 *
 * If the polygon is fully inside an existing zone → "hole" with targetZoneId.
 * Otherwise → "outer".
 */
export function autoClassifyNewPolygon(
	newPoly: Feature<Polygon>,
	existingZones: ZoneCollection,
): { action: "outer" | "hole"; targetZoneId?: string } {
	for (const zone of existingZones.all()) {
		if (polygonContains(zone.geometry, newPoly)) {
			return { action: "hole", targetZoneId: zone.id };
		}
	}
	return { action: "outer" };
}
